import { useQueryClient } from "@tanstack/react-query";
import path from "constants/path";
import { useBookmarks } from "features/Bookmarks";
import {
  getFiltersFromQuery,
  getQueryFromFilters,
} from "features/FilterDrawer/utils/queryParams/filterQueryParams";
import { THREADS_QUERY_KEY } from "features/Sidebar/hooks/useThreads";
import { parseThreadResponse } from "features/Thread/types";
import useCredits from "features/User/hooks/useCredits";
import useIncognitoMode from "features/User/hooks/useIncognitoMode";
import { createBookmarkItemsAPI, createThread } from "helpers/api";
import { IBookmarkItem, ISearchBookmark } from "helpers/bookmark";
import { threadPath } from "helpers/pageUrl";
import useProToggle from "hooks/useProToggle";
import { getThreadQueryKey } from "hooks/useThread";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";

// When a bookmark search item is clicked when threads is enabled, use this hook to parse the search url, create a new thread, replace the old search bookmark with a bookmark of the newly created thread, and redirect to it.
export default function useConvertSearchBookmarkToThread(
  bookmark: IBookmarkItem
) {
  const { deleteBookmarkItem, reloadBookmarkItems } = useBookmarks();
  const router = useRouter();
  const queryClient = useQueryClient();

  const { isPremium, isOutOfProSearchCredits } = useCredits();
  const { isProToggleOn } = useProToggle();
  const { isIncognitoModeActive } = useIncognitoMode();

  const convertSearchBookmarkToThread = async () => {
    // Parse the bookmark's search url into parameters to create a new thread with.
    const bookmarkSearchData: ISearchBookmark =
      bookmark.bookmark_data as ISearchBookmark;

    const queryParams = bookmarkSearchData.search_url.replace(
      `${path.RESULTS}/`,
      ``
    );
    const parsedQuery: ParsedUrlQuery = Object.fromEntries(
      new URLSearchParams(queryParams).entries()
    );
    const filters = getFiltersFromQuery(parsedQuery);
    const filterParams = getQueryFromFilters(filters);

    // Create a new thread from the search bookmark
    const threadResponse = await createThread({
      userMessage: String(parsedQuery.q),
      isProEnabled: Boolean(
        isProToggleOn && (isPremium || !isOutOfProSearchCredits)
      ),
      isIncognito: isIncognitoModeActive,
      filters: filterParams,
    });

    // Cache the thread so we don’t refetch on redirect
    queryClient.setQueryData(
      getThreadQueryKey(threadResponse.thread_id),
      parseThreadResponse(threadResponse)
    );

    // Update the sidebar/thread list
    queryClient.invalidateQueries({
      queryKey: THREADS_QUERY_KEY,
      exact: true,
    });

    // Replace the old bookmark with a new one pointing to this thread
    await createBookmarkItemsAPI([
      {
        list_id: String(bookmark.bookmark_list_id),
        thread_id: threadResponse.thread_id,
      },
    ]);
    await deleteBookmarkItem(bookmark);
    reloadBookmarkItems();

    // Redirect to the new thread
    const redirect = threadPath(threadResponse.thread_id, threadResponse.title);
    router.push(redirect);
  };

  return {
    convertSearchBookmarkToThread,
  };
}
